// Effective-date rules for SKU status changes, shared by the change-availability popover and the edit-product modal.
// Visible for Sale -> Out of Stock has a lead time; every other transition is "future Sundays only".
// Keep in sync with lambdas/src/userApi/products/roasterEffectiveDate.ts, which enforces the lead time on the backend.

export type EffectiveDateRule = "tomorrowOrLater" | "longTerm21Day" | "sundaysOnly";

export const LONG_TERM_OOS_MIN_DAYS = 21;

// react-i18next types t() with literal keys, so a looked-up key needs a plain string-returning view of it.
export type EffectiveDateTranslate = (key: string) => string;

export const EFFECTIVE_DATE_HELPER_KEY: Record<EffectiveDateRule, string> = {
  tomorrowOrLater: "products.changeAvailability.tomorrowOrLater",
  longTerm21Day: "products.changeAvailability.longTermOOSMin",
  sundaysOnly: "products.changeAvailability.sundaysOnly",
};

const isSunday = (date: Date) => date.getDay() === 0;

// Short-term V→OOS lets the user pick any day from tomorrow onward — the
// picker just rejects today and anything in the past.
export const disableBeforeTomorrow = (date: Date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date <= today;
};

// All other transitions (OOS→V both terms, draft/rejected → either) keep the
// original "future Sundays only" rule the BE expects for status changes.
export const disableNonSundays = (date: Date) => {
  if (disableBeforeTomorrow(date)) return true;
  return !isSunday(date);
};

// Long-Term variants going Visible for Sale → Out of Stock require a
// 21-day wind-down window before the change takes effect, so the calendar
// must reject anything earlier than today + 21 days (any day of week).
const disableUnder21Days = (date: Date) => {
  const min = new Date();
  min.setHours(0, 0, 0, 0);
  min.setDate(min.getDate() + LONG_TERM_OOS_MIN_DAYS);
  return date < min;
};

export const buildDisableEffectiveDate =
  (rule: EffectiveDateRule) =>
  (date: Date): boolean => {
    if (rule === "longTerm21Day") return disableUnder21Days(date);
    if (rule === "tomorrowOrLater") return disableBeforeTomorrow(date);
    return disableNonSundays(date);
  };

// First date the calendar accepts under the active rule. Long-term V→OOS:
// today + 21 days. Short-term V→OOS: tomorrow. Everything else (OOS→V both
// terms, draft/rejected): the next future Sunday.
export const getFirstAvailableEffectiveDate = (rule: EffectiveDateRule): Date => {
  const candidate = new Date();
  candidate.setHours(0, 0, 0, 0);
  if (rule === "longTerm21Day") {
    candidate.setDate(candidate.getDate() + LONG_TERM_OOS_MIN_DAYS);
    return candidate;
  }
  if (rule === "tomorrowOrLater") {
    candidate.setDate(candidate.getDate() + 1);
    return candidate;
  }
  // sundaysOnly — walk forward until disableNonSundays accepts the candidate
  // so the auto-fill stays in lock-step with the disable predicate.
  do {
    candidate.setDate(candidate.getDate() + 1);
  } while (disableNonSundays(candidate));
  return candidate;
};

export const resolveEffectiveDateRule = (isShortTerm: boolean, currentStatus: string, nextStatus: string): EffectiveDateRule => {
  // Long-term Visible for Sale → Out of Stock: 21-day wind-down.
  if (!isShortTerm && currentStatus === "published" && nextStatus === "out_of_stock") {
    return "longTerm21Day";
  }
  // Short-term Visible for Sale → Out of Stock: tomorrow or later.
  if (isShortTerm && currentStatus === "published" && nextStatus === "out_of_stock") {
    return "tomorrowOrLater";
  }
  // OOS → V (both terms) and draft/rejected starting points fall back to the
  // original Sunday-only rule.
  return "sundaysOnly";
};
